import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { DeployChannelCountService } from '@gfe/core/services/deploy-plan-config/deploy-channel-count.service';

import { CurrentLangService } from '@core/services/common/current-lang.service';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import {
  PlanFindingsStatusKind,
  selectPlanFindingsStatusInView
} from '@core/store/plan-findings/plan-findings.selectors';
import * as PlanSelectors from '@core/store/plan/plan.selectors';
import { computeOverallDeployStatus } from '@core/utils/deploy-status.utils';
import { deployStatusesOf } from '@core/utils/plan/deploy-statuses';

import { planOverviewCommands } from './plan-overview-link';

type PillTone = 'neutral' | 'danger' | 'warning' | 'success' | 'info';

interface Pill {
  key: string;
  params?: { count: number };
  tone: PillTone;
}

const PILL_CLASSES: Record<PillTone, string> = {
  neutral: 'bg-elevated text-text-secondary',
  danger: 'bg-danger-soft text-danger',
  warning: 'bg-warning-soft text-warning',
  success: 'bg-success-soft text-success',
  info: 'bg-info-soft text-info'
};

const FINDINGS_PILLS: Record<PlanFindingsStatusKind, Pill> = {
  busy: { key: 'common.loading', tone: 'neutral' },
  unavailable: { key: 'features.plan.findings.unavailable', tone: 'neutral' },
  never_checked: { key: 'features.plan.findings.never_checked', tone: 'neutral' },
  blocking: { key: 'features.plan.findings.blocking_label', tone: 'danger' },
  warnings: { key: 'features.plan.findings.warning_label', tone: 'warning' },
  stale: { key: 'features.plan.findings.stale_title', tone: 'info' },
  ok: { key: 'features.plan.no_warnings', tone: 'success' }
};

@Component({
  selector: 'app-plan-info-section',
  imports: [DatePipe, RouterLink, TranslatePipe],
  templateUrl: './plan-info-section.component.html',
  styles: ``
})
export class PlanInfoSectionComponent {
  private readonly store = inject(Store);
  private readonly deployChannels = inject(DeployChannelCountService);
  protected readonly currentLang = inject(CurrentLangService).currentLang;
  protected readonly pillClasses = PILL_CLASSES;

  protected readonly plan = this.store.selectSignal(PlanSelectors.selectPlan);
  private readonly dataspace = this.store.selectSignal(selectDataspaceModel);
  protected readonly findingsStatus = this.store.selectSignal(
    selectPlanFindingsStatusInView
  );
  private readonly deployOverrides = this.store.selectSignal(
    PlanSelectors.selectDeployStatusOverrides
  );

  protected readonly parentCommands = computed(() =>
    planOverviewCommands(
      this.dataspace()?.id,
      this.plan()?.planningSpace.id,
      this.plan()?.parentPlanId
    )
  );

  protected readonly findingsPill = computed<Pill>(() => {
    const { kind, count } = this.findingsStatus();
    return { ...FINDINGS_PILLS[kind], params: count > 0 ? { count } : undefined };
  });

  protected readonly deployPill = computed<Pill | null>(() => {
    const statuses = deployStatusesOf(this.plan() ?? null, this.deployOverrides());
    switch (computeOverallDeployStatus(statuses, this.deployChannels.enabledCount())) {
      case 'SUCCESS':
        return { key: 'features.plan.deploy.status.deployed', tone: 'success' };
      case 'ERROR':
        return { key: 'features.plan.deploy.status.error', tone: 'danger' };
      case 'PROCESSING':
        return { key: 'features.plan.deploy.status.deploying', tone: 'info' };
      default:
        return null;
    }
  });
}
